import React from "react";
import { AlertTriangle, ChartLine, FlaskConical, GitCompare, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { StrategyRdHeadlineStats, StrategyRdSweepTable } from "@/types/strategyRd";
import type { StrategyRdCoachLabAction, StrategyRdCoachResultCard } from "@/types/strategyRdCoach";
import {
  formatStrategyRdPct,
  formatStrategyRdRMultiple,
  formatStrategyRdWinRate,
} from "@/utils/strategyRd";
import { strategyRdJobStageLabel } from "@/utils/strategyRdJobs";
import { PNL_NEGATIVE_COLOR, PNL_POSITIVE_COLOR } from "../../Statistics/components/statsSignedPnlBarUtils";
import { strategyRdBrandActionButtonClassName } from "../strategyRdCardShell";
import { HOLDOUT_VERDICTS, StrategyRdHoldoutVerdictChip, strategyRdHeadlineBrief } from "../StrategyRdHoldoutRow";
import { CONCENTRATION, HALVES, StrategyRdCostStressChip } from "../StrategyRdStability";
import { statsCardHeadlineClassName } from "../../Statistics/components/statisticsCardHeadline";

/** Shown for a stat the backtest didn't report (no em dash in UI copy). */
const EMPTY_STAT = "N/A";

/** Card footer buttons: the app's 32px Lab action button, primary font, sentence case. */
const CARD_ACTION_CLASS = cn(
  strategyRdBrandActionButtonClassName,
  "h-8 !px-3 text-xs normal-case tracking-normal [&>span]:!normal-case"
);

const KIND_LABELS: Record<string, string> = {
  backtest: "Backtest",
  sweep: "Parameter search",
  robustness: "Robustness test",
  search: "Strategy search",
  account: "Account test",
};

const METRIC_LABELS: Record<string, string> = {
  expectancy_r: "Expectancy",
  sum_r: "Total R",
  profit_factor_r: "Profit factor",
};

function fmtNumber(v: number | null | undefined, digits = 2): string | null {
  return v == null || !Number.isFinite(v) ? null : v.toFixed(digits);
}

/** Headline stats: trades, win rate, expectancy, profit factor, max drawdown, return. */
export function strategyRdHeadlineCells(s: StrategyRdHeadlineStats): Array<{ label: string; value: string }> {
  const cells: Array<{ label: string; value: string | null }> = [
    { label: "Trades", value: s.closed_count != null ? String(s.closed_count) : null },
    { label: "Win rate", value: formatStrategyRdWinRate(s.win_rate) },
    { label: "Expectancy", value: formatStrategyRdRMultiple(s.expectancy_r) },
    { label: "Profit factor", value: fmtNumber(s.profit_factor ?? s.profit_factor_r) },
    {
      label: "Max drawdown",
      value:
        s.max_drawdown_pct != null
          ? formatStrategyRdPct(-Math.abs(s.max_drawdown_pct))
          : formatStrategyRdRMultiple(s.max_drawdown_r != null ? -Math.abs(s.max_drawdown_r) : null),
    },
    { label: "Return", value: formatStrategyRdPct(s.return_on_starting_equity_pct) },
  ];
  return cells.filter((c): c is { label: string; value: string } => c.value != null);
}

/** Small equity-curve sparkline (≤60 points). */
export function StrategyRdEquitySparkline({
  points,
  width = 220,
  height = 40,
}: {
  points: ReadonlyArray<{ equity: number }>;
  width?: number;
  height?: number;
}): JSX.Element | null {
  if (points.length < 2) return null;
  const ys = points.map((p) => p.equity);
  const min = Math.min(...ys);
  const max = Math.max(...ys);
  const span = max - min || 1;
  const d = points
    .map((p, i) => {
      const x = (i / (points.length - 1)) * width;
      const y = height - 2 - ((p.equity - min) / span) * (height - 4);
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  const up = ys[ys.length - 1] >= ys[0];
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="h-10 w-full"
      preserveAspectRatio="none"
      role="img"
      aria-label={`Equity curve, ${up ? "up" : "down"} over the backtest`}
      data-strategy-rd-sparkline=""
    >
      <path d={d} fill="none" stroke={up ? "#16a34a" : "#dc2626"} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/**
 * Equity curve in the Lab's "Account over the backtest" style: one line, profit green above
 * the starting balance and red below it (the dashed line), so winning and losing stretches read
 * at a glance.
 */
export function StrategyRdEquityArea({
  points,
  height = 84,
}: {
  points: ReadonlyArray<{ equity: number }>;
  height?: number;
}): JSX.Element | null {
  const uid = React.useId().replace(/:/g, "");
  if (points.length < 2) return null;
  const width = 300;
  const ys = points.map((p) => p.equity);
  const min = Math.min(...ys);
  const max = Math.max(...ys);
  const span = max - min || 1;
  const x = (i: number) => (i / (points.length - 1)) * width;
  const y = (v: number) => 3 + (1 - (v - min) / span) * (height - 6);
  // Smooth path (Catmull-Rom → cubic Bézier), like the Lab chart's monotone line.
  const smooth = (pts: ReadonlyArray<readonly [number, number]>) =>
    pts
      .map(([px, py], i) => {
        if (i === 0) return `M${px.toFixed(1)},${py.toFixed(1)}`;
        const [x0, y0] = pts[i - 2] ?? pts[i - 1];
        const [x1, y1] = pts[i - 1];
        const [x3, y3] = pts[i + 1] ?? [px, py];
        const c1x = x1 + (px - x0) / 6;
        const c1y = y1 + (py - y0) / 6;
        const c2x = px - (x3 - x1) / 6;
        const c2y = py - (y3 - y1) / 6;
        return `C${c1x.toFixed(1)},${c1y.toFixed(1)} ${c2x.toFixed(1)},${c2y.toFixed(1)} ${px.toFixed(1)},${py.toFixed(1)}`;
      })
      .join(" ");
  const eq = points.map((p, i) => [x(i), y(p.equity)] as const);
  const line = smooth(eq);
  const startY = y(ys[0]);
  const area = `${line} L${width},${startY.toFixed(1)} L0,${startY.toFixed(1)} Z`;
  // Where the starting balance sits in the height (0 = top): the colours switch there.
  const split = Math.min(1, Math.max(0, startY / height));
  const up = ys[ys.length - 1] >= ys[0];
  const surf = PNL_POSITIVE_COLOR;
  const red = PNL_NEGATIVE_COLOR;
  return (
    <div className="flex flex-col gap-1" data-strategy-rd-sparkline="">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className="block w-full"
        style={{ height }}
        role="img"
        aria-label={`Account over the backtest, ${up ? "up" : "down"}`}
      >
        <defs>
          <linearGradient id={`f${uid}`} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="0" y2={height}>
            <stop offset="0" stopColor={surf} stopOpacity="0.28" />
            <stop offset={split} stopColor={surf} stopOpacity="0.03" />
            <stop offset={split} stopColor={red} stopOpacity="0.03" />
            <stop offset="1" stopColor={red} stopOpacity="0.2" />
          </linearGradient>
          <linearGradient id={`s${uid}`} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="0" y2={height}>
            <stop offset={split} stopColor={surf} />
            <stop offset={split} stopColor={red} />
          </linearGradient>
        </defs>
        <path d={area} fill={`url(#f${uid})`} />
        <path d={`M0,${startY.toFixed(1)} L${width},${startY.toFixed(1)}`} stroke="currentColor" strokeOpacity="0.3" strokeDasharray="4 4" vectorEffect="non-scaling-stroke" />
        <path d={line} fill="none" stroke={`url(#s${uid})`} strokeWidth={1.8} vectorEffect="non-scaling-stroke" />
        <circle
          cx={eq[eq.length - 1][0]}
          cy={eq[eq.length - 1][1]}
          r={2.6}
          fill={up ? PNL_POSITIVE_COLOR : red}
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </div>
  );
}

function periodLabel(p?: { start?: string | null; end?: string | null } | null): string | null {
  if (!p?.start || !p?.end) return null;
  const f = (iso: string, year: boolean) => {
    const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
    return Number.isNaN(d.getTime())
      ? iso
      : d.toLocaleDateString(undefined, { day: "numeric", month: "short", ...(year ? { year: "numeric" } : {}), timeZone: "UTC" });
  };
  return `${f(p.start, p.start.slice(0, 4) !== p.end.slice(0, 4))} – ${f(p.end, true)}`;
}

function money(v: number): string {
  const sign = v > 0 ? "+" : v < 0 ? "−" : "";
  return `${sign}${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: Math.abs(v) < 1000 ? 2 : 0 })}`;
}

function StatCell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className={cn(statsCardHeadlineClassName, "whitespace-nowrap !text-[11px] !leading-4")}>{label}</dt>
      <dd className="!m-0 !mt-1 whitespace-nowrap font-[family-name:var(--font-secondary)] text-[17px] font-medium tabular-nums text-gray-600 dark:text-foreground">
        {children}
      </dd>
    </div>
  );
}

const TONE_BY_COLOR: Record<string, "good" | "bad" | "neutral"> = { "#16a34a": "good", "#dc2626": "bad" };

const BADGE_TONE = {
  good: "bg-[#06E363]/12 text-[#049a44] dark:text-[#3df08a] before:bg-[#16a34a]",
  bad: "bg-[#F70009]/10 text-[#c8000a] dark:text-[#ff5a5f] before:bg-[#dc2626]",
  neutral: "bg-muted text-gray-500 dark:text-muted-foreground before:bg-gray-400",
} as const;

/** Square status badge with a dot (the Lab's verdict labels and tips). */
function CheckBadge({
  verdict,
  map,
  suffix,
  attr,
}: {
  verdict: string | null | undefined;
  map: Record<string, { label: string; color: string; tip: string }>;
  suffix?: string | null;
  attr?: string;
}): JSX.Element | null {
  if (!verdict) return null;
  const v = map[verdict] ?? { label: verdict.replace(/_/g, " "), color: "", tip: "" };
  const tone = TONE_BY_COLOR[v.color.toLowerCase()] ?? "neutral";
  return (
    <span
      title={v.tip || undefined}
      className={cn(
        "inline-flex h-5 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[4px] px-1.5",
        "font-[family-name:var(--font-primary)] text-[11px] before:size-1.5 before:rounded-full before:content-['']",
        BADGE_TONE[tone]
      )}
      data-strategy-rd-verdict={verdict}
      {...(attr ? { [attr]: verdict } : {})}
    >
      {v.label}
      {suffix ? ` · ${suffix}` : ""}
    </span>
  );
}

/** One robustness check: what was tested, the figures under it, the verdict on the right. */
function CheckRow({ label, detail, title, children }: { label: string; detail?: string | null; title?: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3" title={title}>
      <div className="min-w-0">
        <p className="!m-0 text-xs leading-5 text-gray-500 dark:text-muted-foreground">{label}</p>
        {detail ? (
          <p className="!m-0 font-[family-name:var(--font-secondary)] text-[11px] leading-4 tabular-nums text-gray-400">{detail}</p>
        ) : null}
      </div>
      {children}
    </div>
  );
}

const UNSEEN_STATUS: Record<string, { label: string; tone: keyof typeof BADGE_TONE }> = {
  held_up: { label: "Held up on unseen data", tone: "good" },
  weaker: { label: "Weaker on unseen data", tone: "neutral" },
  failed: { label: "Failed on unseen data", tone: "bad" },
};

function paramsLabel(params: Record<string, unknown>): string {
  return Object.entries(params)
    .map(([path, v]) => `${path.split(".").slice(-2).join(".")} = ${String(v)}`)
    .join(", ");
}

function sameParams(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  const ka = Object.keys(a);
  return ka.length === Object.keys(b).length && ka.every((k) => String(a[k]) === String(b[k]));
}

const SWEEP_TH = "!h-8 !px-2.5 !py-0 whitespace-nowrap !font-medium text-gray-500 dark:text-muted-foreground";

/** Parameter search: params, tuning metric, unseen metric, verdict — best row highlighted. */
export function StrategyRdSweepTableView({ sweep }: { sweep: StrategyRdSweepTable }): JSX.Element {
  const metric = sweep.metric;
  const fmt = (s: StrategyRdHeadlineStats | null | undefined) => {
    const v = s?.[metric as keyof StrategyRdHeadlineStats];
    return metric === "profit_factor_r" ? fmtNumber(v) ?? EMPTY_STAT : formatStrategyRdRMultiple(v) ?? EMPTY_STAT;
  };
  return (
    <div className="overflow-x-auto rounded-[6px] !border !border-solid !border-border" data-strategy-rd-sweep="">
      <table className="w-full !border-collapse font-[family-name:var(--font-primary)] text-[11px] text-gray-500 dark:text-muted-foreground">
        <thead className="bg-muted/50">
          <tr>
            <th className={cn(SWEEP_TH, "text-left")}>Settings</th>
            <th className={cn(SWEEP_TH, "text-right")}>Tuning {METRIC_LABELS[metric] ?? metric}</th>
            <th className={cn(SWEEP_TH, "text-right")}>Unseen</th>
            <th className={cn(SWEEP_TH, "text-left")}>Verdict</th>
          </tr>
        </thead>
        <tbody>
          {sweep.combinations.map((row, i) => {
            const best = sameParams(row.params, sweep.best);
            return (
              <tr
                key={i}
                className={cn(
                  "!border-0 !border-t !border-solid !border-border/70",
                  best && "bg-[color:var(--brand-tropical-surf)]/[0.06] font-medium text-gray-600 dark:text-foreground"
                )}
                data-strategy-rd-sweep-best={best ? "" : undefined}
              >
                <td className="!px-2.5 !py-1.5">
                  {paramsLabel(row.params)}
                  {best ? <span className="ml-1 text-[color:var(--brand-tropical-surf)]">· best, saved</span> : null}
                </td>
                <td className="!px-2.5 !py-1.5 text-right tabular-nums">{fmt(row.split?.tuning ?? row.stats)}</td>
                <td className="!px-2.5 !py-1.5 text-right tabular-nums">{fmt(row.split?.unseen)}</td>
                <td className="!px-2.5 !py-1.5">
                  <StrategyRdHoldoutVerdictChip verdict={row.split?.verdict} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ResultCardBody({
  card,
  onOpen,
  onCompare,
}: {
  card: StrategyRdCoachResultCard;
  onOpen?: (hypothesisId: string) => void;
  onCompare?: (hypothesisId: string) => void;
}): JSX.Element {
  const s = card.stats;
  const closed = s.closed_count ?? null;
  const wins = closed != null && s.win_rate != null ? Math.round(s.win_rate * closed) : null;
  const pf = s.profit_factor ?? s.profit_factor_r ?? null;
  const net = card.net_pnl ?? s.sum_pnl ?? null;
  const period = periodLabel(card.period);
  const meta = [card.market?.symbol, card.market?.candle_type, period].filter(Boolean).join(" · ");
  const tone = (v: number | null | undefined, pivot = 0) =>
    v == null ? "" : v > pivot ? "text-[color:var(--chart-positive,#10b981)]" : v < pivot ? "text-[color:var(--chart-negative,#ef4444)]" : "";
  const split = card.split;
  const halves = card.stability?.halves;
  const conc = card.stability?.concentration;
  const status = split?.verdict ? UNSEEN_STATUS[split.verdict] : undefined;
  const holdoutPct = split?.holdout_pct != null ? Math.round(split.holdout_pct) : null;
  const unseenR = split?.unseen?.expectancy_r;
  const hasChecks = Boolean(split || halves || conc || card.robustness?.cost_stress || card.robustness?.verdict);
  const canCompare = onCompare && card.version != null && card.version > 1;
  return (
    <div className="flex flex-col">
      <div className="flex items-start justify-between gap-3 !px-4 !pb-3 !pt-3.5">
        <div className="min-w-0">
          <p className="!m-0 flex min-w-0 items-center gap-1.5 font-[family-name:var(--font-primary)] !text-[15px] !font-medium leading-6 text-[color:var(--brand-tropical-surf)]">
            <span className="truncate">{card.name || "Backtest"}</span>
            {card.version != null ? (
              <span className="shrink-0 rounded-[4px] bg-muted px-1.5 py-px text-[10px] font-medium text-gray-500">
                v{card.version}
              </span>
            ) : null}
          </p>
          {meta ? (
            <p className="!m-0 !mt-0.5 font-[family-name:var(--font-secondary)] text-[11px] tabular-nums text-gray-400" data-strategy-rd-card-period="">
              {meta}
            </p>
          ) : null}
        </div>
        {status ? (
          <span
            className={cn(
              "mt-0.5 hidden h-5 shrink-0 items-center gap-1.5 rounded-[4px] px-1.5 font-[family-name:var(--font-primary)] text-[11px] @[22rem]/labcard:inline-flex",
              "before:size-1.5 before:rounded-full before:content-['']",
              BADGE_TONE[status.tone]
            )}
          >
            {status.label}
          </span>
        ) : null}
      </div>
      <dl className="!m-0 grid grid-cols-2 gap-x-4 gap-y-4 !border-0 !border-t !border-solid !border-border !px-4 !py-3.5 @[22rem]/labcard:grid-cols-3">
        <StatCell label="Net P&L">
          <span className={tone(net)}>{net != null ? money(net) : formatStrategyRdPct(s.return_on_starting_equity_pct) ?? EMPTY_STAT}</span>
        </StatCell>
        <StatCell label="Trades">{closed ?? EMPTY_STAT}</StatCell>
        <StatCell label="Win rate">
          {formatStrategyRdWinRate(s.win_rate) ?? EMPTY_STAT}
          {wins != null && closed != null ? (
            <span className="ml-1 text-[11px] font-normal text-gray-400">{wins}|{closed - wins}</span>
          ) : null}
        </StatCell>
        <StatCell label="Max drawdown">
          <span className={s.max_drawdown_pct ? "text-[color:var(--chart-negative,#ef4444)]" : ""}>
            {s.max_drawdown_pct != null ? formatStrategyRdPct(-Math.abs(s.max_drawdown_pct)) : EMPTY_STAT}
          </span>
        </StatCell>
        <StatCell label="Profit factor">
          <span className={tone(pf, 1)}>{fmtNumber(pf) ?? EMPTY_STAT}</span>
        </StatCell>
        <StatCell label="Return">
          <span className={tone(s.return_on_starting_equity_pct)}>{formatStrategyRdPct(s.return_on_starting_equity_pct) ?? EMPTY_STAT}</span>
        </StatCell>
      </dl>
      {hasChecks ? (
        <div className="flex flex-col gap-3 !border-0 !border-t !border-solid !border-border !px-4 !py-3.5">
          {split ? (
            <CheckRow
              label={`Unseen data${holdoutPct != null ? ` (last ${holdoutPct}%)` : ""}`}
              detail={`Tuning ${strategyRdHeadlineBrief(split.tuning)} · unseen ${strategyRdHeadlineBrief(split.unseen)}`}
              title={`The last ${holdoutPct ?? 30}% of the window was held out: a change that only works in the tuning period shows up here.`}
            >
              <CheckBadge
                verdict={split.verdict}
                map={HOLDOUT_VERDICTS}
                suffix={split.verdict === "failed" && unseenR != null ? formatStrategyRdRMultiple(unseenR) : null}
                attr="data-strategy-rd-holdout-verdict"
              />
            </CheckRow>
          ) : null}
          {halves ? (
            <CheckRow
              label="First half vs second half"
              detail={`${strategyRdHeadlineBrief(halves.first)} → ${strategyRdHeadlineBrief(halves.second)}`}
              title="Same locked trades, split at the midpoint of setup time. Not a new scan."
            >
              <CheckBadge verdict={halves.verdict} map={HALVES} attr="data-strategy-rd-halves-verdict" />
            </CheckRow>
          ) : null}
          {conc ? (
            <CheckRow
              label={conc.dropped != null ? `Without the best ${conc.dropped} trade${conc.dropped === 1 ? "" : "s"}` : "Without the best trades"}
              detail={
                conc.expectancy_r_without_best != null
                  ? `${formatStrategyRdRMultiple(conc.expectancy_r_without_best)} / trade`
                  : null
              }
            >
              <CheckBadge verdict={conc.verdict} map={CONCENTRATION} attr="data-strategy-rd-concentration-verdict" />
            </CheckRow>
          ) : null}
          {card.robustness?.cost_stress || card.robustness?.verdict ? (
            <CheckRow
              label="Costs and robustness"
              detail={
                card.robustness.verdict
                  ? `Robustness: ${card.robustness.verdict}${card.robustness.stale ? " (for an older backtest)" : ""}`
                  : null
              }
            >
              <StrategyRdCostStressChip verdict={card.robustness.cost_stress} />
            </CheckRow>
          ) : null}
          <p className="!m-0 text-[11px] leading-4 text-gray-400" data-strategy-rd-judge-hint="">
            Judge by hold-out, halves, concentration and costs, not the headline.
          </p>
        </div>
      ) : null}
      {card.sweep ? (
        <div className="!border-0 !border-t !border-solid !border-border !px-4 !py-3.5">
          <StrategyRdSweepTableView sweep={card.sweep} />
        </div>
      ) : null}
      {onOpen || canCompare ? (
        <div className="flex flex-wrap items-center justify-between gap-2 !border-0 !border-t !border-solid !border-border bg-muted/30 !px-4 !py-3">
          <p className="!m-0 flex min-w-0 items-center gap-1.5 text-xs text-gray-500 dark:text-muted-foreground">
            <ChartLine className="size-3.5 shrink-0 text-[color:var(--brand-tropical-surf)]" aria-hidden />
            <span className="truncate">Full results open in the Lab</span>
          </p>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {/* Only once there is an original to compare with (v2 and later). */}
            {canCompare ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className={cn(CARD_ACTION_CLASS, "!px-2.5 !text-gray-500 hover:!text-[color:var(--brand-tropical-surf)] [&_svg]:!text-current")}
                onClick={() => onCompare!(card.hypothesis_id)}
              >
                <GitCompare className="size-3.5" aria-hidden />
                Compare with original
              </Button>
            ) : null}
            {onOpen ? (
              <Button
                type="button"
                variant="outline-brand"
                size="sm"
                className={CARD_ACTION_CLASS}
                onClick={() => onOpen(card.hypothesis_id)}
              >
                <FlaskConical className="size-3.5" aria-hidden />
                {card.version != null && card.version > 1 ? `Open v${card.version} in Lab` : "Open in Lab"}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** One card per TES lab action: progress text while it runs, then the result card. */
export function StrategyRdCoachLabActionCard({
  action,
  onOpen,
  onCompare,
}: {
  action: StrategyRdCoachLabAction;
  onOpen?: (hypothesisId: string) => void;
  onCompare?: (hypothesisId: string) => void;
}): JSX.Element {
  const running = action.status === "queued" || action.status === "running";
  const title = KIND_LABELS[action.kind] ?? action.kind;
  return (
    <div className="flex flex-col gap-2" data-strategy-rd-lab-action={action.kind} data-status={action.status}>
      <p className={cn(statsCardHeadlineClassName, "!m-0 !text-[color:var(--brand-tropical-surf)]")}>{title} by TES</p>
      <div
        className={cn(
          "@container/labcard overflow-hidden rounded-[8px] !border !border-solid !border-border bg-card font-[family-name:var(--font-primary)]",
          !(action.card && !running && action.status !== "failed") && "!p-4"
        )}
      >
        {running ? (
          <div className="flex flex-col gap-2" role="status">
            <p className="!m-0 flex items-center gap-2 text-xs text-gray-500 dark:text-muted-foreground">
              <Loader2 className="size-3.5 animate-spin text-[color:var(--brand-tropical-surf)]" aria-hidden />
              {strategyRdJobStageLabel(action.progress) || action.message || "Queued…"}
            </p>
            <div className="h-1 overflow-hidden rounded-full bg-muted">
              <div
                className={cn(
                  "h-full rounded-full bg-[image:var(--gradient-primary)] transition-[width] duration-500",
                  action.progress?.total ? "" : "w-1/3 animate-pulse"
                )}
                style={
                  action.progress?.total
                    ? { width: `${Math.min(100, Math.round(((action.progress.done ?? 0) / action.progress.total) * 100))}%` }
                    : undefined
                }
              />
            </div>
          </div>
        ) : action.status === "failed" ? (
          <p className="!m-0 flex items-center gap-2 text-xs text-destructive">
            <AlertTriangle className="size-3.5" aria-hidden />
            {action.message || "The job failed."}
          </p>
        ) : action.card ? (
          <ResultCardBody card={action.card} onOpen={onOpen} onCompare={onCompare} />
        ) : (
          <div className="flex flex-col gap-3">
            <p className="!m-0 text-xs leading-5 text-gray-500 dark:text-muted-foreground">
              {action.message || (action.outcome === "no_setups" ? "No trade-ready setups in this window." : "Done.")}
            </p>
            {action.hypothesis_id && onOpen ? (
              <Button
                type="button"
                variant="outline-brand"
                size="sm"
                className={cn(CARD_ACTION_CLASS, "w-fit")}
                onClick={() => onOpen(action.hypothesis_id!)}
              >
                <FlaskConical className="size-3.5" aria-hidden />
                Open in Lab
              </Button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
